import {
  addDays,
  formatDateHeading,
  monthName,
  rtaState,
  todayIn,
  type ActivityDto,
  type HomeDto,
} from '@koverta/shared';
import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import {
  Avatar,
  Avatars,
  CatPill,
  Dialog,
  Flag,
  Icon,
  Sheet,
  Spinner,
  hhmm,
  relTime,
} from '../components/ui';
import { post } from '../lib/api';
import { applyTheme, useClickOutside, useIsMobile } from '../lib/hooks';
import { useHousehold, type Fmt } from '../lib/household';
import { useHome, useScheduled } from '../lib/queries';
import { usePlanModel } from './plan-model';
import '../styles/loans.css';

const greeting = () => {
  const h = new Date().getHours();
  return h < 5 ? 'Good evening' : h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
};

export function HomePage() {
  const q = useHome();
  const mobile = useIsMobile();
  if (q.isPending) return <Spinner />;
  if (!q.data) return <div className="banner warn">Couldn't load Home.</div>;
  return mobile ? <HomeMobile h={q.data} /> : <HomeDesktop h={q.data} />;
}

function useParts(h: HomeDto) {
  const { fmt, today, members } = useHousehold();
  const navigate = useNavigate();
  const [cover, setCover] = useState<string | null>(null);
  const st = rtaState(h.rta);
  const rta = (
    <div className={`rta-hero ${st}`} data-testid="rta">
      <small>
        {h.rta < 0 ? 'Assigned more than you have' : 'Ready to Assign'} · {monthName(h.month)}
      </small>
      <div className="n">
        {fmt.num(h.rta)} <span>{fmt.base}</span>
      </div>
      <div className="f">
        <span>
          {h.fxThisMonth ? (
            <>
              <Icon name="swap" size={13} /> incl. FX revaluation {fmt.num(h.fxThisMonth, 0, 'always')}
            </>
          ) : null}
        </span>
        {h.rta !== 0 ? (
          <button className="btn primary" onClick={() => navigate('/plan')}>
            {h.rta < 0 ? 'Fix this' : 'Assign'}
          </button>
        ) : null}
      </div>
    </div>
  );
  const attCount = h.overspent.length + (h.underfunded.count ? 1 : 0) + (h.inbox.count ? 1 : 0);
  const att = (
    <div className="card h-card">
      <div className="h4">
        Needs attention <span>{attCount}</span>
      </div>
      {h.overspent.map((c) => (
        <div className="att" key={c.id}>
          <span className="dot" style={{ background: 'var(--neg)' }} />
          <div className="t">
            {c.name}
            <small>Overspent by {fmt.num(-c.available)}</small>
          </div>
          <button className="btn" onClick={() => setCover(c.id)}>
            Cover
          </button>
        </div>
      ))}
      {h.underfunded.count ? (
        <div className="att">
          <span className="dot" style={{ background: 'var(--warn)' }} />
          <div className="t">
            {h.underfunded.count} {h.underfunded.count === 1 ? 'category' : 'categories'} underfunded
            <small>
              {fmt.num(h.underfunded.amount)} {fmt.base} needed to meet targets
            </small>
          </div>
          <button className="btn" onClick={() => navigate('/plan')}>
            Review
          </button>
        </div>
      ) : null}
      {h.inbox.count ? (
        <div className="att">
          <span className="dot" style={{ background: 'var(--brand)' }} />
          <div className="t">
            {h.inbox.count} payment{h.inbox.count === 1 ? '' : 's'} in Inbox
            {h.inbox.latest ? (
              <small>
                Latest: {h.inbox.latest.payeeName},{' '}
                {fmt.money(Math.abs(h.inbox.latest.amount), h.inbox.latest.currency, 2)} ·{' '}
                {hhmm(h.inbox.latest.at)}
              </small>
            ) : null}
          </div>
          <button className="btn" onClick={() => navigate('/inbox')}>
            Open
          </button>
        </div>
      ) : null}
      {!attCount ? (
        <div className="muted" style={{ fontSize: 13.5, padding: '4px 0' }}>
          Nothing needs you right now. ✓
        </div>
      ) : null}
    </div>
  );
  const tiles = h.pinned.length ? (
    <div className="tiles">
      {h.pinned.map((c) => (
        <div
          className="tile"
          key={c.id}
          onClick={() => navigate('/plan')}
          role="button"
          style={{ cursor: 'pointer' }}
        >
          <small>{c.name}</small>
          <b className={c.available < 0 ? 'neg' : ''}>{fmt.num(c.available)}</b>
          <div className="of">
            {c.available < 0
              ? `overspent · ${fmt.num(c.total)} assigned`
              : `left of ${fmt.num(c.total)} ${fmt.base}`}
          </div>
          <div className={`bar ${c.state}`}>
            <i
              style={{
                width: `${c.available < 0 ? 100 : Math.max(3, (c.available / Math.max(1, c.total)) * 100)}%`,
              }}
            />
          </div>
        </div>
      ))}
    </div>
  ) : (
    <div className="card h-card muted" style={{ fontSize: 13 }}>
      Pin categories from the Plan inspector to watch them here.
    </div>
  );
  const aomValues = h.ageOfMoney.history.map((v) => v ?? 0);
  const max = Math.max(30, ...aomValues);
  const pts = aomValues
    .map((v, i) => `${(i / Math.max(1, aomValues.length - 1)) * 150},${36 - (v / max) * 30}`)
    .join(' ');
  const aom = (
    <div className="card h-card">
      <div className="h4">Age of money</div>
      <div className="aom">
        <b>
          {h.ageOfMoney.days ?? '—'} <span>days</span>
        </b>
        <svg width="150" height="40" viewBox="0 0 150 40" aria-hidden="true">
          <polyline fill="none" stroke="var(--brand)" strokeWidth="2" points={pts} />
          <circle
            cx="150"
            cy={36 - ((aomValues[aomValues.length - 1] ?? 0) / max) * 30}
            r="3"
            fill="var(--brand)"
          />
        </svg>
        {h.ageOfMoney.deltaVsLastMonth !== null ? (
          <span
            className={h.ageOfMoney.deltaVsLastMonth >= 0 ? 'pos-t' : 'neg-t'}
            style={{ fontSize: 12.5, marginLeft: 'auto' }}
          >
            {h.ageOfMoney.deltaVsLastMonth >= 0 ? '+' : '−'}
            {Math.abs(h.ageOfMoney.deltaVsLastMonth)} vs last month
          </span>
        ) : null}
      </div>
    </div>
  );
  const feed = (
    <div className="card h-card">
      <div className="h4">Recent activity</div>
      {h.activity.length ? (
        h.activity.map((e) => <FeedItem key={e.id} e={e} fmt={fmt} today={today} names={members} />)
      ) : (
        <div className="muted" style={{ fontSize: 13 }}>
          Nothing yet.
        </div>
      )}
    </div>
  );
  const coverSheet = cover ? <CoverSheet id={cover} month={h.month} onClose={() => setCover(null)} /> : null;
  // Net worth: budget money plus other assets minus loans (loans.md §5).
  const debtNote = h.netWorth.loans.length
    ? ` · after ${h.netWorth.loans.map((l) => `${l.name} ${fmt.num(l.base)}`).join(', ')}`
    : h.netWorth.debts
      ? ` · after debts ${fmt.num(h.netWorth.debts)}`
      : '';
  const netWorth = (
    <div className="h-sub" data-testid="home-net-worth">
      Net worth {fmt.money(h.netWorth.total, fmt.base)}
      {debtNote}
    </div>
  );
  return { rta, att, tiles, aom, feed, coverSheet, netWorth, upcoming: <Upcoming /> };
}

/** Scheduled transactions due in the next 7 days; renders nothing when none are due. */
function Upcoming() {
  const { fmt, today } = useHousehold();
  const navigate = useNavigate();
  const until = addDays(today, 7);
  const items = (useScheduled().data ?? [])
    .flatMap((s) => s.nextOccurrences.filter((d) => d <= until).map((date) => ({ s, date })))
    .sort((a, b) => (a.date < b.date ? -1 : 1));
  if (!items.length) return null;
  return (
    <div className="card h-card" data-testid="home-upcoming">
      <div className="h4">
        Upcoming this week <span>{items.length}</span>
      </div>
      <div className="upc">
        {items.slice(0, 6).map(({ s, date }) => (
          <div
            className="u"
            key={`${s.id}-${date}`}
            role="button"
            style={{ cursor: 'pointer' }}
            onClick={() => navigate(`/accounts/${s.accountId}`)}
          >
            <span>
              {date === today ? 'Today' : fmt.date(date)} · {s.payeeName || s.categoryName || s.accountName}
            </span>
            <b className={s.amount > 0 ? 'pos-t' : ''}>{fmt.money(s.amount, s.currency, 2)}</b>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Cover overspending from Home: same choices as the Plan popover, in a sheet (mobile) or dialog (desktop). */
function CoverSheet({ id, month, onClose }: { id: string; month: string; onClose: () => void }) {
  const model = usePlanModel(month);
  const { fmt } = useHousehold();
  const mobile = useIsMobile();
  const c = model.byId.get(id);
  if (!c) return null;
  const short = -c.available;
  const sources = model.cats
    .filter((x) => x.id !== c.id && x.available > 0 && !x.hidden)
    .sort((a, b) => b.available - a.available)
    .slice(0, 6);
  const take = async (from: string, amount: number) => {
    onClose();
    await model.move(from, c.id, amount);
  };
  const body = (
    <>
      <div className="pop-h" style={{ marginBottom: 6 }}>
        <span>
          {c.name} is short{' '}
          <b className="neg-t">
            {fmt.num(Math.max(0, short))} {fmt.base}
          </b>
          . Take it from:
        </span>
      </div>
      <div className="pop-l" style={{ margin: '4px -6px 8px' }}>
        {sources.map((x) => (
          <button key={x.id} className="pop-i" onClick={() => take(x.id, Math.min(short, x.available))}>
            <span>{x.name}</span>
            <CatPill c={x} />
          </button>
        ))}
      </div>
      <div className="pop-f">
        <span>Ready to Assign: {fmt.num(model.rta)}</span>
        <button onClick={() => take('rta', short)}>Use Ready to Assign</button>
      </div>
    </>
  );
  if (short <= 0) return null;
  return mobile ? (
    <Sheet onClose={onClose} label="Cover overspending">
      <div className="sh-h" style={{ marginBottom: 8 }}>
        <b>Cover overspending</b>
      </div>
      {body}
    </Sheet>
  ) : (
    <Dialog title="Cover overspending" onClose={onClose} size="sm">
      {body}
    </Dialog>
  );
}

function FeedItem({
  e,
  fmt,
  today,
  names,
}: {
  e: ActivityDto;
  fmt: Fmt;
  today: string;
  names: Map<string, { displayName: string }>;
}) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- payload shape depends on the event kind
  const p = e.payload as Record<string, any>;
  const who = names.get(e.memberId)?.displayName ?? 'Someone';
  const day = todayIn('Europe/Belgrade', new Date(e.at));
  const when = day === today ? hhmm(e.at) : day === addDays(today, -1) ? 'Yesterday' : fmt.date(day);
  let text: ReactNode = null;
  let sub = when;
  let amount: number | null = null;
  switch (e.kind) {
    case 'tx.created':
      text = (
        <>
          {who} added <b>{p.payee || 'a transaction'}</b>
        </>
      );
      sub = [p.split ? 'Split' : p.category, when].filter(Boolean).join(' · ');
      amount = p.baseAmount;
      break;
    case 'capture.approved':
      text = (
        <>
          {who} approved <b>{p.payee}</b>
        </>
      );
      sub = [p.category, when].filter(Boolean).join(' · ');
      amount = p.baseAmount;
      break;
    case 'capture.dismissed':
      text = (
        <>
          {who} dismissed <b>{p.merchant}</b>
        </>
      );
      break;
    case 'capture.merged':
      text = (
        <>
          {who} merged <b>{p.merchant}</b> with an imported transaction
        </>
      );
      break;
    case 'money.moved':
      text = (
        <>
          {who} moved <b>{fmt.num(p.amount)}</b>
        </>
      );
      sub = `${p.from} → ${p.to} · ${when}`;
      break;
    case 'money.assigned':
      text = (
        <>
          {who} assigned <b>{fmt.num(p.amount)}</b> to {p.category}
        </>
      );
      break;
    case 'money.auto_assigned':
      text = (
        <>
          {who} auto-assigned <b>{fmt.num(p.amount, 0, 'always')}</b>
        </>
      );
      sub = `${p.count} categories · ${when}`;
      break;
    case 'transfer.created':
      text = (
        <>
          {who} transferred <b>{fmt.money(p.fromAmount, p.fromCurrency)}</b>
        </>
      );
      sub = `${p.from} → ${p.to} · ${when}`;
      amount = p.toCurrency === fmt.base ? p.toAmount : null;
      break;
    case 'target.set':
      text = (
        <>
          {who} set a target for <b>{p.category}</b>
        </>
      );
      break;
    case 'category.deleted':
      text = (
        <>
          {who} deleted the category <b>{p.category}</b>
        </>
      );
      sub = [
        p.movedTo
          ? `moved to ${p.movedTo}`
          : p.deletedTransactions
            ? `${p.deletedTransactions} transactions deleted too`
            : null,
        when,
      ]
        .filter(Boolean)
        .join(' · ');
      break;
    case 'category_group.deleted':
      text = (
        <>
          {who} deleted the group <b>{p.group}</b>
        </>
      );
      break;
    case 'account.created':
      text = (
        <>
          {who} added the account <b>{p.name}</b>
        </>
      );
      break;
    case 'account.reconciled':
      text = (
        <>
          {who} reconciled <b>{p.name}</b>
        </>
      );
      break;
    case 'import.completed':
      text = (
        <>
          {who} imported <b>{p.created}</b> transaction{p.created === 1 ? '' : 's'}{' '}
          {p.source === 'ynab' ? 'from YNAB' : <>into {p.account}</>}
        </>
      );
      sub =
        p.source === 'ynab'
          ? `${p.verified ? 'Every number matches YNAB' : 'Check the import report in Settings'} · ${when}`
          : `${p.matched} matched captured payments · ${when}`;
      break;
    case 'tx.updated':
      text = <>{who} edited a transaction</>;
      break;
    case 'tx.deleted':
      text = <>{who} deleted a transaction</>;
      break;
    default:
      text = <>{who} made a change</>;
  }
  return (
    <div className="feed">
      <Avatar id={e.memberId} lg />
      <div className="t">
        {text}
        <small>{sub}</small>
      </div>
      {amount !== null && amount !== undefined ? (
        <span className={`a ${amount > 0 ? 'pos-t' : ''}`}>{fmt.num(amount, 2)}</span>
      ) : null}
    </div>
  );
}

function HomeMobile({ h }: { h: HomeDto }) {
  const { member, today } = useHousehold();
  const parts = useParts(h);
  return (
    <>
      <div className="greet">
        <h1>
          <small>{formatDateHeading(today)}</small>
          {greeting()}, {member.displayName}
        </h1>
        <Avatars lg />
        <Overflow />
      </div>
      {parts.rta}
      {parts.att}
      {parts.upcoming}
      <MoneyCard h={h} netWorth={parts.netWorth} />
      <div className="h4" style={{ margin: '6px 18px 8px' }}>
        Pinned
      </div>
      {parts.tiles}
      {parts.aom}
      {parts.feed}
      {parts.coverSheet}
    </>
  );
}

function Overflow() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const ref = useClickOutside<HTMLDivElement>(() => setOpen(false), open);
  return (
    <div style={{ position: 'relative' }} ref={ref}>
      <button className="ib" aria-label="More" aria-expanded={open} onClick={() => setOpen(!open)}>
        <Icon name="more" />
      </button>
      {open ? (
        <div className="menu" role="menu">
          <button role="menuitem" onClick={() => navigate('/reflect')}>
            <Icon name="reflect" /> Reflect
          </button>
          <button role="menuitem" onClick={() => navigate('/predictions')}>
            <Icon name="forecast" /> Predictions
          </button>
          <button role="menuitem" onClick={() => navigate('/settings')}>
            <Icon name="settings" /> Settings
          </button>
          <button
            role="menuitem"
            onClick={() => applyTheme(document.documentElement.dataset.theme === 'dark' ? 'light' : 'dark')}
          >
            <Icon name="eye" /> Toggle dark mode
          </button>
          <button
            role="menuitem"
            onClick={async () => {
              await post('/api/auth/logout');
              location.href = '/';
            }}
          >
            <Icon name="logout" /> Sign out
          </button>
        </div>
      ) : null}
    </div>
  );
}

/** Mobile: money in budget accounts, with net worth under it. */
function MoneyCard({ h, netWorth }: { h: HomeDto; netWorth: ReactNode }) {
  const { fmt } = useHousehold();
  return (
    <div className="card h-card">
      <div className="kv">
        <span>Money in budget accounts</span>
        <b>{fmt.money(h.budgetTotalBase, fmt.base)}</b>
      </div>
      {netWorth}
    </div>
  );
}

function HomeDesktop({ h }: { h: HomeDto }) {
  const { member, today, fmt, members } = useHousehold();
  const parts = useParts(h);
  const partner = h.partnerLastActive ? members.get(h.partnerLastActive.memberId) : null;
  return (
    <>
      <div className="pg-h" style={{ padding: '24px 32px 18px', margin: 0 }}>
        <div>
          <h2>
            {greeting()}, {member.displayName}
          </h2>
          <div className="sub">
            {formatDateHeading(today)} {today.slice(0, 4)}
            {partner && h.partnerLastActive
              ? ` · ${partner.displayName} was active ${relTime(h.partnerLastActive.at)}`
              : ''}
          </div>
        </div>
      </div>
      <div className="home-d">
        <div className="col">
          {parts.rta}
          {parts.att}
          {parts.upcoming}
          {parts.feed}
        </div>
        <div className="col">
          <div>
            <div className="h4">Pinned categories</div>
            {parts.tiles}
          </div>
          {parts.aom}
          <div className="card h-card">
            <div className="h4">Accounts by currency</div>
            {h.accountsByCurrency.map((c) => (
              <div className="kv" key={c.currency}>
                <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <Flag currency={c.currency} /> {c.currency} accounts
                </span>
                <b>
                  {fmt.money(c.native, c.currency)}
                  {c.currency !== fmt.base ? (
                    <span className="muted" style={{ fontWeight: 400 }}>
                      {' '}
                      ≈ {fmt.num(c.base)}
                    </span>
                  ) : null}
                </b>
              </div>
            ))}
            <div className="kv" style={{ borderTop: '1px solid var(--line)', marginTop: 6, paddingTop: 8 }}>
              <span>Money in budget accounts</span>
              <b>{fmt.money(h.budgetTotalBase, fmt.base)}</b>
            </div>
            {parts.netWorth}
          </div>
        </div>
      </div>
      {parts.coverSheet}
    </>
  );
}
